/** A tile: one number and a label of two to four words - ADR-0212 decision 2.
 *
 * **A tile never sums.** The number arrives as the server's string, already formatted by the caller
 * (the same rule `DataTable` has for its counts), so a tile cannot disagree with the table beneath it
 * about how many there are. It is a number and a label; anything longer belongs behind an
 * `InfoPopover`, which a tile can carry (`infoKey`). A figure that has a receipt passes `onOpen`, and
 * the number becomes a button; without it the number is static and does not look like a link.
 *
 * `data-tile` is the attribute the text-budget guard (`textBudget.ts`) excludes: a tile's words are
 * not body text. The label is checked in development (`LABEL_MAX_WORDS`) and throws, so a sentence
 * cannot be put in a tile and shipped.
 *
 * A strip holds three to five tiles. Fewer reads as a gap and more as a dashboard; `TileStrip` says
 * so in development as well.
 */

import { Children } from "react";
import type { ReactNode } from "react";

import { quantity } from "../../lib/format";
import { NO_RECEIPT, noReceipt } from "../../lib/noReceipt";
import { ReadsFailed } from "../Loaded";
import type { ReadStatus } from "../Loaded";
import type { InfoKey } from "../../shell/infoText";
import { InfoPopover } from "./InfoPopover";
import styles from "./Tile.module.css";

export const LABEL_MAX_WORDS = 4;
export const TILES_MIN = 3;
export const TILES_MAX = 5;

/** The number of words in a label, split on whitespace. */
export function labelWords(label: string): number {
  return label.trim().split(/\s+/).filter(Boolean).length;
}

/** A count is printed with its thousands separators: `1865 of 2018` is `1,865 of 2,018`. Only a value that
 * is a whole number, or two of them joined by "of" (with an optional "+"), is touched, so a version
 * ("v3"), an entry number ("#15004") or a date is never reformatted. The tile never sums or rounds. */
const COUNT_TEXT = /^\d+(?: of \d+)?\+?$/;
function tileText(value: string): string {
  return COUNT_TEXT.test(value) ? value.replace(/\d{4,}/g, (digits) => quantity(digits)) : value;
}

/** What kind of thing a tile's value is, so a state never looks like a result: a number is the
 * headline it always was; a dash is "no figure yet" and is quiet; a word ("Not assessed", "None") is a
 * state and is set at the size of a label, not of a total. A value is a word when it holds no digit. */
export type FigureKind = "number" | "none" | "word";

export function figureKind(value: string): FigureKind {
  const text = value.trim();
  if (/^[-–—]$/.test(text)) return "none";
  return /\d/.test(text) ? "number" : "word";
}

export type DeltaDirection = "up" | "down" | "flat";
export type DeltaTone = "good" | "bad" | "neutral";

/** What moved since the comparison period, as the caller states it.
 *
 * **The words and the direction are the server's** (a tile never computes a difference), and the
 * tone is the caller's judgement of whether that direction is good news: an emissions total going
 * down is "good" and a count of missing bills going down is "good" too, so the component cannot
 * know. It is drawn as an arrow AND a word AND a tint, never colour alone, and the figure itself is
 * never coloured by it (the brand and status colours do not paint an emissions total). */
export interface TileDelta {
  /** "12%" or "340 t": already formatted, with no sign - the direction carries it. */
  value: string;
  direction: DeltaDirection;
  tone?: DeltaTone;
  /** What it is against: "vs last year". Read out and shown small. */
  against?: string;
  /** How complete the period it is against is, when it is not whole: "Prior period: 4 of 12 months
   * recorded". A change against a part-year reads as a surge without it (G-Wviewer-07). Shown small
   * under the change and read out with it. */
  note?: string;
}

const DELTA_WORD: Record<DeltaDirection, string> = { up: "up", down: "down", flat: "no change" };
const DELTA_GLYPH: Record<DeltaDirection, string> = { up: "▲", down: "▼", flat: "–" };

/** The fewest points a tile's line is drawn for. Two or three near-equal points scaled to the box drew a
 * full-height "V" that read as a tick or a crash (design review, 2026-11-03); four is the least that is a
 * shape rather than a gesture. */
export const SPARK_MIN_POINTS = 4;

/** A series of numbers drawn as a small line: the shape of the last few periods, not a chart. It is
 * decoration with a text alternative (`sparkLabel`), carries no axis and no value, and is skipped
 * for fewer than `SPARK_MIN_POINTS` points. **The baseline is zero** (or the series' own minimum when
 * a value is negative), never the series' minimum: scaled min-to-max, a rise from 1,000 to 1,010 filled
 * the box and read as a surge. The numbers are the server's series; nothing is derived here beyond
 * scaling them to the box. Grey, because it is a hint and the colour of a mark belongs to the chart. */
function Sparkline({ series, label }: { series: readonly number[]; label: string }) {
  if (series.length < SPARK_MIN_POINTS) return null;
  const floor = Math.min(0, ...series);
  const max = Math.max(0, ...series);
  const span = max - floor || 1;
  const w = 80;
  const h = 24;
  const at = (index: number, value: number): [number, number] => [
    (index / (series.length - 1)) * w,
    h - 2 - ((value - floor) / span) * (h - 4),
  ];
  const points = series.map((value, index) => at(index, value).map((n) => n.toFixed(1)).join(",")).join(" ");
  const [endX, endY] = at(series.length - 1, series[series.length - 1] ?? 0);
  return (
    <svg
      className={styles.spark}
      viewBox={`0 0 ${String(w)} ${String(h)}`}
      width={w}
      height={h}
      role="img"
      aria-label={label}
      data-spark-points={series.length}
    >
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
      <circle className={styles.sparkEnd} cx={endX.toFixed(1)} cy={endY.toFixed(1)} r="2" />
    </svg>
  );
}

export interface TileProps {
  /** The number, as the caller formats it. A dash for a figure that could not be read, never `0`. */
  value: string;
  /** Two to four words. */
  label: string;
  /** A unit set small beside the number: `tCO2e`. */
  unit?: string;
  /** Makes the number a button that opens where the figure came from. */
  onOpen?: () => void;
  openLabel?: string;
  /** The button's test id, for a figure the browser gate must find as a button (rule 3). */
  openTestId?: string;
  /** The way to the rows behind the figure, built by the caller (a router `Link`). */
  link?: ReactNode;
  /** @deprecated No effect. The link row used to reserve this many lines so the links of a strip
   * started on one line, which made Home's tiles 182px tall with 126px empty (design review,
   * 2026-11-03). A tile is now as tall as its content, at least `--tile-min`, and a strip's tiles
   * are one height by stretching. Callers may delete the prop. */
  linkLines?: number;
  /** The read behind this figure has not answered. A grey bar stands where the number will be, and
   * the dash is kept for a figure that was read and is absent (the product's "no figure yet"). */
  loading?: boolean;
  /** The read the figure is drawn from, where it is a read of its own (a count, a total beside the
   * table's list). While it is out the tile is `loading`; once it failed the tile keeps its dash and
   * says so with `data-unread="failed"`, and the page names the failure once - `TileStrip`'s `reads`.
   * A dash standing for "not read yet" or "could not be read" and drawn exactly like "read, and nothing
   * there" is how a failed request came to look like a figure (E2E P9, G-P9-09, -10). */
  read?: { isPending: boolean; error: unknown };
  /** An explanation behind a small info control. */
  infoKey?: InfoKey;
  /** What moved since the comparison period (the dashboard band's KPI tile). */
  delta?: TileDelta;
  /** The last few periods as a small line. The server's series, drawn and not derived.
   *
   * **Only when there is a series to draw, and not above a chart of the same months.** A line is drawn
   * if and only if this is passed with `SPARK_MIN_POINTS` or more, so a caller must not pass one for a
   * *state count* (bills waiting, sources missing): a count of what is outstanding now has no trend the
   * product can state, and a line beside it would invent one. A tile directly over the monthly chart
   * (Home, the scope pages) shows the change chip and no line: the chart is the line. */
  spark?: readonly number[];
  /** The line's text alternative: "Monthly, January to June". */
  sparkLabel?: string;
  testId?: string;
  /** `data-kpi`: the name a screen test or the browser gate finds a headline figure by. */
  kpi?: string;
}

export function Tile({
  value,
  label,
  unit,
  onOpen,
  openLabel,
  openTestId,
  link,
  infoKey,
  delta,
  spark,
  sparkLabel,
  loading: loadingProp = false,
  read,
  testId,
  kpi,
}: TileProps) {
  const failed = Boolean(read?.error);
  const loading = loadingProp || (read !== undefined && read.isPending && !failed);
  if (import.meta.env.DEV && labelWords(label) > LABEL_MAX_WORDS) {
    throw new Error(
      `Tile label "${label}" is ${String(labelWords(label))} words; a tile label is at most ${String(LABEL_MAX_WORDS)} (ADR-0212). Move the rest behind an info control.`,
    );
  }
  const shown = tileText(value);
  const kind = figureKind(value);
  // A dash is "no figure yet", and carries no unit: "- tCO2e" reads as a measurement of nothing.
  const figure = (
    <>
      {shown}
      {unit && kind !== "none" ? (
        <span className={styles.unit}>
          {/* The gap is the margin; this space is for the words a screen reader and a copy-paste get
              ("2,423.3 tCO2e", not "2,423.3tCO2e": review item 19). */}
          <span className="visuallyHidden"> </span>
          {unit}
        </span>
      ) : null}
    </>
  );
  return (
    <div
      className={`card ${styles.tile}`}
      data-tile={label}
      data-testid={testId}
      data-kpi={kpi}
      data-unread={failed ? "failed" : undefined}
      aria-busy={loading ? "true" : undefined}
    >
      <div className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        {infoKey ? <InfoPopover infoKey={infoKey} /> : null}
      </div>
      {loading ? (
        // A grey bar in the figure's slot, the height of the number it stands for: the page does not
        // move when the answer lands, and a dash (which means "read, and nothing there") is never
        // shown for a read that has not finished.
        <div className={styles.figure} data-kind="loading" data-testid="tile-loading">
          <span className={`skeleton ${styles.bone}`} aria-hidden="true" />
        </div>
      ) : onOpen ? (
        <button
          type="button"
          className={`${styles.figure} num`}
          data-kind={kind}
          onClick={onOpen}
          data-testid={openTestId}
          aria-label={openLabel ?? `${label}: ${shown}. Show where this came from`}
        >
          {figure}
        </button>
      ) : (
        <div className={`${styles.figure} num`} data-kind={kind} title={shown.length > 9 ? shown : undefined}>
          {figure}
        </div>
      )}
      {!loading && (delta || (spark && spark.length >= SPARK_MIN_POINTS)) ? (
        <>
        <div className={styles.trend}>
          {delta ? (
            <span
              className={styles.delta}
              data-direction={delta.direction}
              data-tone={delta.tone ?? "neutral"}
              data-testid="tile-delta"
              {...noReceipt(NO_RECEIPT.difference)}
            >
              <span aria-hidden="true">{DELTA_GLYPH[delta.direction]}</span>
              <span className="visuallyHidden">{DELTA_WORD[delta.direction]} </span>
              {delta.value}
              {delta.against ? <span className={styles.against}> {delta.against}</span> : null}
            </span>
          ) : (
            <span />
          )}
          {spark && spark.length >= SPARK_MIN_POINTS ? (
            <Sparkline series={spark} label={sparkLabel ?? `${label}, recent periods`} />
          ) : null}
        </div>
        {delta?.note ? (
          <div className={styles.deltaNote} data-testid="tile-delta-note">
            {delta.note}
          </div>
        ) : null}
        </>
      ) : null}
      {link && !loading ? <div className={styles.link}>{link}</div> : null}
    </div>
  );
}

export function TileStrip({
  children,
  label = "Summary",
  reads,
}: {
  children: ReactNode;
  label?: string;
  /** The reads behind the strip's figures that are not the page's own main read. Each one that
   * failed is said above the strip, once, with its Try again (`ReadsFailed`): a tile whose read failed
   * shows a dash, and a dash with no sentence beside it reads as a figure. */
  reads?: ReadonlyArray<{ of: string; read: ReadStatus }>;
}) {
  const count = Children.count(children);
  if (import.meta.env.DEV && (count < TILES_MIN || count > TILES_MAX)) {
    throw new Error(
      `A tile strip holds ${String(TILES_MIN)} to ${String(TILES_MAX)} tiles (ADR-0212); this one has ${String(count)}.`,
    );
  }
  const strip = (
    <section className={styles.strip} aria-label={label} data-tile-count={count}>
      {children}
    </section>
  );
  return reads ? (
    <>
      <ReadsFailed reads={reads} testId="tile-failures" />
      {strip}
    </>
  ) : (
    strip
  );
}

/** A strip of tiles with no figure yet: each a label over a quiet dash (KD, 2026-11-02). A place that
 * has nothing to read - no reporting period, no bills - still shows what it will count, and a dash is
 * "no figure yet", never a measured zero. */
export function DashTiles({ labels, label = "Summary" }: { labels: readonly string[]; label?: string }) {
  return (
    <TileStrip label={label}>
      {labels.map((text) => (
        <Tile key={text} value="-" label={text} />
      ))}
    </TileStrip>
  );
}
